import configApi from "./api";
import { createSlice } from "@reduxjs/toolkit";
import { toast } from "react-toastify";

const initialConfig = {
  config: {},
  customModuleItems: [],
  isLoading: false,
  isProcessing: false,
  isCompleted: false,
  isItemsLoading: false,
  isItemsProcessing: false,
};

const config = createSlice({
  name: "configs",
  initialState: initialConfig,
  reducers: {
    setIsLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    setIsProcessing: (state, action) => {
      state.isProcessing = action.payload;
    },
    setConfig: (state, action) => {
      state.config = action.payload;
    },
    // Custom Module Items reducers
    setIsItemsLoading: (state, action) => {
      state.isItemsLoading = action.payload;
    },
    setIsItemsProcessing: (state, action) => {
      state.isItemsProcessing = action.payload;
    },
    setCustomModuleItems: (state, action) => {
      state.customModuleItems = action.payload;
    },
    addCustomModuleItem: (state, action) => {
      state.customModuleItems.push(action.payload);
      // Re-sort by priority (ascending: lower numbers first)
      state.customModuleItems.sort(
        (a, b) => (a.priority || 0) - (b.priority || 0)
      );
    },
    updateCustomModuleItem: (state, action) => {
      const { itemId, item } = action.payload;
      const index = state.customModuleItems.findIndex((i) => i._id === itemId);
      if (index !== -1) {
        state.customModuleItems[index] = item;
        // Re-sort by priority (ascending: lower numbers first)
        state.customModuleItems.sort(
          (a, b) => (a.priority || 0) - (b.priority || 0)
        );
      }
    },
    removeCustomModuleItem: (state, action) => {
      const itemId = action.payload;
      state.customModuleItems = state.customModuleItems.filter(
        (item) => item._id !== itemId
      );
    },
  },
});

const { reducer, actions } = config;

export const {
  setIsLoading,
  setIsProcessing,
  setConfig,
  setIsItemsLoading,
  setIsItemsProcessing,
  setCustomModuleItems,
  addCustomModuleItem,
  updateCustomModuleItem,
  removeCustomModuleItem,
} = actions;

export const fetchConfig = () => {
  return async (dispatch) => {
    dispatch(setIsLoading(true));
    try {
      const data = await configApi.getConfig();
      dispatch(setConfig(data.data));
    } catch (err) {
      console.log(err);
    } finally {
      dispatch(setIsLoading(false));
    }
  };
};

export const updateConfig = (config) => {
  return async (dispatch) => {
    try {
      dispatch(setIsProcessing(true));
      const data = await configApi.update(config);
      if (data.error) {
        toast.error(data.error.msg);
      } else {
        dispatch(setConfig(data.data));
        toast.success("Thiết lập cấu hình thành công!");
      }
    } catch (err) {
      console.log(err);
    } finally {
      dispatch(setIsProcessing(false));
    }
  };
};

// Custom Module Items async actions
export const fetchCustomModuleItems = () => {
  return async (dispatch) => {
    dispatch(setIsItemsLoading(true));
    try {
      const response = await configApi.getCustomModuleItems();
      dispatch(setCustomModuleItems(response.data || []));
    } catch (err) {
      console.log(err);
      toast.error("Không thể tải danh sách items!");
    } finally {
      dispatch(setIsItemsLoading(false));
    }
  };
};

export const createCustomModuleItem = (itemData) => {
  return async (dispatch) => {
    dispatch(setIsItemsProcessing(true));
    try {
      console.log("Creating custom module item with data:", itemData);

      const formData = new FormData();

      // Append image if exists
      if (itemData.image) {
        console.log("Appending image to FormData:", itemData.image);
        formData.append("image", itemData.image);
      }

      // Append other data including priority
      Object.keys(itemData).forEach((key) => {
        if (key !== "image") {
          console.log(`Appending ${key}:`, itemData[key]);
          formData.append(key, itemData[key]);
        }
      });

      // Log FormData contents
      for (let pair of formData.entries()) {
        console.log("FormData entry:", pair[0], pair[1]);
      }

      const response = await configApi.createCustomModuleItem(formData);
      console.log("API response:", response);

      if (response.error) {
        toast.error(response.error.msg);
      } else {
        dispatch(addCustomModuleItem(response.data));
        toast.success("Tạo item thành công!");
      }
    } catch (err) {
      console.log("Error creating item:", err);
      toast.error("Không thể tạo item!");
    } finally {
      dispatch(setIsItemsProcessing(false));
    }
  };
};

export const updateCustomModuleItemAction = (itemId, itemData) => {
  return async (dispatch) => {
    dispatch(setIsItemsProcessing(true));
    try {
      const formData = new FormData();

      // Append image if exists (File object or string path)
      if (itemData.image) {
        // If it's a File object, append directly
        // If it's a string (existing image path), append as string
        formData.append("image", itemData.image);
      }

      // Append other data including priority
      Object.keys(itemData).forEach((key) => {
        if (key !== "image") {
          formData.append(key, itemData[key]);
        }
      });

      const response = await configApi.updateCustomModuleItem(itemId, formData);

      if (response.error) {
        toast.error(response.error.msg);
      } else {
        dispatch(updateCustomModuleItem({ itemId, item: response.data }));
        toast.success("Cập nhật item thành công!");
      }
    } catch (err) {
      console.log(err);
      toast.error("Không thể cập nhật item!");
    } finally {
      dispatch(setIsItemsProcessing(false));
    }
  };
};

export const deleteCustomModuleItem = (itemId) => {
  return async (dispatch) => {
    dispatch(setIsItemsProcessing(true));
    try {
      const response = await configApi.deleteCustomModuleItem(itemId);

      if (response.error) {
        toast.error(response.error.msg);
      } else {
        dispatch(removeCustomModuleItem(itemId));
        toast.success("Xóa item thành công!");
      }
    } catch (err) {
      console.log(err);
      toast.error("Không thể xóa item!");
    } finally {
      dispatch(setIsItemsProcessing(false));
    }
  };
};

export default reducer;
